import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FlaggedCommentsService } from '../flagged-comments.service';
import { AlertController, ToastController } from '@ionic/angular';
import { Router } from '@angular/router';

@Component({
  selector: 'app-flagged-comments',
  templateUrl: './flagged-comments.component.html',
  styleUrls: ['./flagged-comments.component.scss'],
})
export class FlaggedCommentsComponent implements OnInit, OnDestroy {
  comments: any[] = [];
  isLoading = true;
  private destroy$ = new Subject<void>();

  constructor(
    private flaggedCommentsService: FlaggedCommentsService,
    private alertController: AlertController,
    private toastController: ToastController,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadFlaggedComments();
  }

  loadFlaggedComments() {
    this.isLoading = true;

    this.flaggedCommentsService
      .getFlaggedComments()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        comments => {
          this.comments = comments;
          this.isLoading = false;
        },
        error => {
          console.error('Error loading flagged comments:', error);
          this.isLoading = false;
          this.presentToast('Error loading comments', 'danger');
        }
      );
  }

  async approveComment(comment) {
    try {
      await this.flaggedCommentsService.approveComment(comment);
      this.comments = this.comments.filter(c => c.id !== comment.id);
      this.presentToast('Comment has been approved');
    } catch (error) {
      console.error('Error approving comment:', error);
      this.presentToast('Error approving comment', 'danger');
    }
  }

  async confirmRejectComment(comment) {
    const alert = await this.alertController.create({
      header: 'Confirm Rejection',
      message: 'Are you sure you want to reject and remove this comment?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Reject Comment',
          handler: () => {
            this.rejectComment(comment);
          },
        },
      ],
    });

    await alert.present();
  }

  async rejectComment(comment) {
    try {
      await this.flaggedCommentsService.rejectComment(comment);
      this.comments = this.comments.filter(c => c.id !== comment.id);
      this.presentToast('Comment has been rejected and removed');
    } catch (error) {
      console.error('Error rejecting comment:', error);
      this.presentToast('Error rejecting comment', 'danger');
    }
  }

  async presentToast(message: string, color: string = 'success') {
    const toast = await this.toastController.create({
      message,
      duration: 2000,
      position: 'bottom',
      color: color,
    });
    toast.present();
  }

  /**
   * Navigate to the item (post, event, etc.) where the comment exists
   * and focus on the comments section
   */
  navigateToComment(comment: any) {
    if (!comment || !comment.postId) {
      this.presentToast('Unable to locate the comment', 'warning');
      return;
    }

    let route = '';
    // Determine the route based on the item type
    if (comment.itemType === 'events') {
      route = `/events/${comment.postId}?focusComments=true`;
    } else if (comment.itemType === 'pools') {
      route = `/pool/${comment.postId}?focusComments=true`;
    } else if (comment.itemType === 'pods') {
      route = `/pod/${comment.postId}?focusComments=true`;
    } else {
      // Default to media route if type not specified
      route = `/media/gallery/${comment.postId}?focusComments=true`;
    }

    this.router.navigateByUrl(route);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

